import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import type { Shipment, ShipmentDetail, TrackingLink } from '../api/types'
import {
  Empty,
  ErrorBanner,
  Loading,
  Panel,
  StatusChip,
  dateTime,
  duration,
  inr,
  num,
} from '../components/ui'

type Filter = 'all' | 'in_flight' | 'delivered' | 'late' | 'failed'

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'in_flight', label: 'In flight' },
  { key: 'delivered', label: 'Delivered' },
  { key: 'late', label: 'Delivered late' },
  { key: 'failed', label: 'Not delivered' },
]

function matches(shipment: Shipment, filter: Filter): boolean {
  switch (filter) {
    case 'in_flight':
      return !['delivered', 'closed', 'cancelled', 'failed'].includes(shipment.status)
    case 'delivered':
      return ['delivered', 'closed'].includes(shipment.status)
    case 'late':
      return shipment.sla_breached === 1
    case 'failed':
      return ['failed', 'cancelled'].includes(shipment.status)
    default:
      return true
  }
}

/** Where a consignment has got to, as a customer would narrate it. */
function ProgressLine({ shipment }: { shipment: ShipmentDetail }) {
  const steps = [
    { label: 'Booked', at: shipment.dispatched_at ? true : shipment.status !== 'cancelled' },
    { label: 'Dispatched', at: Boolean(shipment.dispatched_at) },
    { label: 'In transit', at: Boolean(shipment.dispatched_at) && shipment.status !== 'ready_to_dispatch' },
    { label: 'Delivered', at: Boolean(shipment.delivered_at) },
  ]

  const failed = ['failed', 'cancelled'].includes(shipment.status)

  return (
    <div className="timeline">
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1
        const state = step.at ? 'done' : failed && isLast ? 'failed' : ''

        return (
          <div key={step.label} className={`tl-item ${state}`}>
            <div className="tl-title">{step.label}</div>
            <div className="tl-time">
              {step.label === 'Dispatched'
                ? dateTime(shipment.dispatched_at)
                : step.label === 'Delivered'
                  ? shipment.delivered_at
                    ? dateTime(shipment.delivered_at)
                    : failed
                      ? 'not delivered'
                      : `expected ${dateTime(shipment.promised_at)}`
                  : step.at
                    ? 'done'
                    : 'pending'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function ShipmentDrawer({ shipmentId, onClose }: { shipmentId: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  const detailQuery = useQuery({
    queryKey: ['portal-shipment', shipmentId],
    queryFn: () => api.get<ShipmentDetail>(`/portal/shipments/${shipmentId}`).then((r) => r.data),
  })

  const linkQuery = useQuery({
    queryKey: ['portal-tracking-link', shipmentId],
    queryFn: () =>
      api.get<TrackingLink>(`/portal/shipments/${shipmentId}/tracking-link`).then((r) => r.data),
  })

  const shipment = detailQuery.data

  // The API attaches the approved POD to the detail. It is null until a driver
  // has captured one and a human has approved it — a capture still under review
  // is not yet proof of anything.
  const pod = shipment?.pod ?? null

  // The portal lives under /portal/ (BASE_URL); a bare /track/… would open the console.
  const shareUrl = linkQuery.data ? `${window.location.origin}${import.meta.env.BASE_URL}track/${linkQuery.data.token}` : ''

  async function copy() {
    if (!shareUrl) return
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard access can be refused; the field is selectable either way.
      setCopied(false)
    }
  }

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer" onClick={(e) => e.stopPropagation()}>
        <div className="drawer-head">
          <div className="row">
            <div>
              <div className="page-title mono">{shipment?.shipment_number ?? 'Consignment'}</div>
              <div className="page-sub">
                {shipment ? `${shipment.origin_name} → ${shipment.destination_name}` : ''}
              </div>
            </div>
            <span className="spacer" />
            <button className="btn btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        <div className="drawer-body">
          {detailQuery.isLoading ? (
            <Loading rows={6} />
          ) : detailQuery.error ? (
            <ErrorBanner error={detailQuery.error} />
          ) : shipment ? (
            <div className="stack">
              {shipment.sla_breached === 1 && (
                <div className="banner banner-warn" style={{ marginBottom: 0 }}>
                  Delivered {duration(shipment.delay_minutes)} against the agreed transit time.
                  {' '}If this cost you, you can raise a claim from the Claims tab.
                </div>
              )}

              <Panel title="Where it is">
                <ProgressLine shipment={shipment} />
              </Panel>

              <Panel title="Consignment">
                <dl className="kv">
                  <dt>Status</dt>
                  <dd>
                    <StatusChip status={shipment.status} />
                  </dd>
                  <dt>LR number</dt>
                  <dd className="mono">{shipment.lr_number ?? '—'}</dd>
                  <dt>Service</dt>
                  <dd>
                    {(shipment.service_level ?? '—').replace(/_/g, ' ')} ·{' '}
                    {(shipment.shipment_type ?? '').toUpperCase()}
                  </dd>
                  <dt>Weight</dt>
                  <dd>{num(shipment.total_weight_kg, 0)} kg</dd>
                  <dt>Packages</dt>
                  <dd>
                    {shipment.total_packages ?? '—'}
                    {shipment.pallets ? ` · ${shipment.pallets} pallets` : ''}
                  </dd>
                  <dt>Distance</dt>
                  <dd>{num(shipment.planned_distance_km, 0)} km</dd>
                  {Number(shipment.cod_total) > 0 && (
                    <>
                      <dt>Cash on delivery</dt>
                      <dd>{inr(shipment.cod_total)}</dd>
                    </>
                  )}
                  <dt>Promised by</dt>
                  <dd>{dateTime(shipment.promised_at)}</dd>
                  <dt>Delivered</dt>
                  <dd>
                    {shipment.delivered_at ? dateTime(shipment.delivered_at) : 'not yet'}
                    {shipment.delivered_at && shipment.sla_breached === 1 && (
                      <span style={{ color: '#fbbf24' }}> · {duration(shipment.delay_minutes)}</span>
                    )}
                  </dd>
                  {/* The vehicle, not the driver: a customer is owed the truck's
                      identity, not an employee's name and number. */}
                  <dt>Vehicle</dt>
                  <dd className="mono">{shipment.vehicle_number ?? 'not yet assigned'}</dd>
                </dl>
              </Panel>

              <Panel title={`Stops (${shipment.stops.length})`} bodyless>
                <table className="table">
                  <thead>
                    <tr>
                      <th className="num">#</th>
                      <th>Where</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Completed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shipment.stops.map((stop) => (
                      <tr key={stop.id}>
                        <td data-label="#" className="num">
                          {stop.sequence}
                        </td>
                        <td data-label="Where">
                          {stop.party_name}
                          <div className="page-sub">{stop.address_json?.city ?? ''}</div>
                        </td>
                        <td data-label="Type" style={{ color: 'var(--text-dim)' }}>
                          {stop.stop_type}
                        </td>
                        <td data-label="Status">
                          <StatusChip status={stop.status} />
                        </td>
                        <td data-label="Completed">{dateTime(stop.completed_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Panel>

              {shipment.items.length > 0 && (
                <Panel title="Goods" bodyless>
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th className="num">Quantity</th>
                        <th className="num">Weight</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.items.map((item) => (
                        <tr key={item.id}>
                          <td data-label="Description">{item.description ?? '—'}</td>
                          <td data-label="Quantity" className="num">
                            {num(item.quantity, 0)}
                          </td>
                          <td data-label="Weight" className="num">
                            {num(item.weight_kg, 0)} kg
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Panel>
              )}

              {pod && (
                <Panel title="Proof of delivery">
                  <dl className="kv">
                    <dt>Received by</dt>
                    <dd>
                      {pod.receiver_name ?? '—'}
                      {pod.otp_verified === 1 && (
                        <span className="chip chip-green" style={{ marginLeft: 8 }}>
                          OTP verified
                        </span>
                      )}
                    </dd>
                    <dt>Received at</dt>
                    <dd>{dateTime(pod.received_at ?? pod.captured_at)}</dd>
                    <dt>Delivered quantity</dt>
                    <dd>{num(pod.delivered_qty, 0)}</dd>
                    {Number(pod.short_qty) > 0 && (
                      <>
                        <dt>Short</dt>
                        <dd style={{ color: '#fbbf24' }}>{num(pod.short_qty, 0)}</dd>
                      </>
                    )}
                    {Number(pod.damaged_qty) > 0 && (
                      <>
                        <dt>Damaged</dt>
                        <dd style={{ color: '#fbbf24' }}>{num(pod.damaged_qty, 0)}</dd>
                      </>
                    )}
                    {Number(pod.cod_due) > 0 && (
                      <>
                        <dt>Cash collected</dt>
                        <dd>
                          {inr(pod.cod_collected)} of {inr(pod.cod_due)}
                          {pod.cod_mode ? ` · ${pod.cod_mode}` : ''}
                        </dd>
                      </>
                    )}
                    {pod.remarks && (
                      <>
                        <dt>Remarks</dt>
                        <dd>{pod.remarks}</dd>
                      </>
                    )}
                  </dl>
                </Panel>
              )}

              <Panel title="Share this consignment">
                <div className="page-sub" style={{ marginBottom: 10 }}>
                  Anyone with this link can follow the consignment without signing in. Useful for a
                  consignee who does not have a portal account.
                </div>
                {linkQuery.isLoading ? (
                  <Loading rows={1} />
                ) : linkQuery.error ? (
                  <ErrorBanner error={linkQuery.error} />
                ) : (
                  <div className="copyfield">
                    <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} />
                    <button className="btn btn-sm" onClick={copy}>
                      {copied ? 'Copied' : 'Copy'}
                    </button>
                  </div>
                )}
              </Panel>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function ShipmentsPage() {
  const [filter, setFilter] = useState<Filter>('all')
  const [open, setOpen] = useState<string | null>(null)

  const shipmentsQuery = useQuery({
    queryKey: ['portal-shipments'],
    queryFn: () => api.get<Shipment[]>('/portal/shipments').then((r) => r.data),
  })

  const all = shipmentsQuery.data ?? []
  const shown = all.filter((shipment) => matches(shipment, filter))

  return (
    <div className="stack">
      <div className="page-head">
        <div className="page-title">Shipments</div>
        <div className="page-sub">Every consignment this transporter has carried for you</div>
      </div>

      <div className="filters">
        {FILTERS.map((option) => (
          <button
            key={option.key}
            className={`filter-chip ${filter === option.key ? 'active' : ''}`}
            onClick={() => setFilter(option.key)}
          >
            {option.label} ({all.filter((s) => matches(s, option.key)).length})
          </button>
        ))}
      </div>

      <Panel title={`${shown.length} consignment(s)`} bodyless>
        {shipmentsQuery.isLoading ? (
          <Loading />
        ) : shipmentsQuery.error ? (
          <div style={{ padding: 16 }}>
            <ErrorBanner error={shipmentsQuery.error} />
          </div>
        ) : shown.length === 0 ? (
          <Empty
            title="Nothing here"
            hint={filter === 'all' ? 'No consignments booked yet.' : 'Try another filter.'}
          />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Consignment</th>
                <th>Lane</th>
                <th>Status</th>
                <th>Promised</th>
                <th>Delivered</th>
                <th className="num">Weight</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((shipment) => (
                <tr
                  key={shipment.id}
                  className="clickable"
                  onClick={() => setOpen(shipment.id)}
                >
                  <td data-label="Consignment" className="mono">
                    {shipment.shipment_number}
                    {shipment.sla_breached === 1 && (
                      <span className="chip chip-amber" style={{ marginLeft: 6 }}>
                        late
                      </span>
                    )}
                  </td>
                  <td data-label="Lane">
                    {shipment.origin_name} → {shipment.destination_name}
                  </td>
                  <td data-label="Status">
                    <StatusChip status={shipment.status} />
                  </td>
                  <td data-label="Promised">{dateTime(shipment.promised_at)}</td>
                  <td data-label="Delivered">{dateTime(shipment.delivered_at)}</td>
                  <td data-label="Weight" className="num">
                    {num(shipment.total_weight_kg, 0)} kg
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {open && <ShipmentDrawer shipmentId={open} onClose={() => setOpen(null)} />}
    </div>
  )
}
